import { useQueries } from "@tanstack/react-query";
import type { EmailTemplate } from "@/types/settings";
import type { SubscriptionSettingsView } from "@/types/subscription";
import {
  computeAdminSettingsStatus,
  type AdminSettingsStatusResult,
} from "@/lib/admin-settings-status";

const PLATFORM_REQUIRED_FIELDS = [
  "bank_name",
  "bank_account_number",
  "bank_recipient_name",
] as const;

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed: ${url}`);
  return (await res.json()) as T;
}

export function useAdminSettingsStatus(): {
  status: AdminSettingsStatusResult | null;
  isLoading: boolean;
  isError: boolean;
} {
  const results = useQueries({
    queries: [
      {
        queryKey: ["country-multipliers"],
        queryFn: () => getJson<Array<unknown>>("/api/country-multipliers"),
      },
      {
        queryKey: ["admin", "enrollment-questionnaire"],
        queryFn: () =>
          getJson<{ data: { questions: unknown[] } }>("/api/admin/enrollment-questionnaire"),
      },
      {
        queryKey: ["admin", "specializations"],
        queryFn: () => getJson<{ data: unknown[] }>("/api/admin/specializations"),
      },
      {
        queryKey: ["admin", "email-templates"],
        queryFn: () => getJson<EmailTemplate[]>("/api/email-templates"),
      },
      {
        queryKey: ["admin", "subscription-settings"],
        queryFn: () => getJson<SubscriptionSettingsView>("/api/admin/subscription-settings"),
      },
      {
        queryKey: ["admin", "settings"],
        queryFn: () => getJson<Record<string, string>>("/api/settings"),
      },
    ],
  });

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);
  if (isLoading || results.some((r) => r.data === undefined)) {
    return { status: null, isLoading, isError };
  }

  // Order must match the queries array above.
  const [multipliers, questionnaire, specializations, templates, billing, settings] =
    results.map((r) => r.data) as [
      unknown[],
      { data: { questions: unknown[] } },
      { data: unknown[] },
      EmailTemplate[],
      SubscriptionSettingsView,
      Record<string, string>,
    ];

  const billingConfigured = Boolean(
    billing.priceAnnual > 0 &&
      billing.priceSemiannual > 0 &&
      billing.issuer.businessName.trim() &&
      billing.issuer.businessAddress.trim() &&
      billing.issuer.taxId.trim() &&
      billing.issuer.iban.trim(),
  );

  const platformMissingFields = PLATFORM_REQUIRED_FIELDS.filter(
    (key) => !settings[key]?.trim(),
  );

  const status = computeAdminSettingsStatus({
    multiplierCount: multipliers.length,
    questionnaireQuestionCount: questionnaire.data.questions.length,
    specializationCount: specializations.data.length,
    emptyEmailTemplateCount: templates.filter((t) => !t.body?.trim()).length,
    billingConfigured,
    platformMissingFields,
  });

  return { status, isLoading: false, isError: false };
}
